/**
 * Router.jsx — top-level route definitions
 *
 * Public SEO routes (crawlable, per-page <title> + structured data):
 *   /                    → MarketingScreen (landing page)
 *   /articles            → ArticlesPage (listing)
 *   /articles/:slug      → ArticlePage  (single article, Article schema)
 *   /worksheets          → WorksheetsPage (listing)
 *   /worksheets/:id      → WorksheetPage  (single, LearningResource JSON-LD)
 *   /worksheets/holidays[/:slug] → WorksheetHolidayPage (holiday hub + per-holiday pages)
 *
 * App routes (auth-gated SPA, no individual SEO needed):
 *   /app                 → App (the full game SPA)
 *   /login               → redirects into /app (App handles its own login state)
 *   *                    → NotFound (noindex; nginx serves a real 404 for dead content URLs)
 */
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Spinner } from "./components/UI";

// Without this, navigating from the bottom of a long listing (e.g. /worksheets)
// opens the next page at the same scroll offset — the user lands on the footer.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Public pages — code-split for performance
const MarketingScreen = lazy(() => import("./screens/MarketingScreen"));
const ArticlesPage    = lazy(() => import("./pages/ArticlesPage"));
const ArticlePage     = lazy(() => import("./pages/ArticlePage"));
const WorksheetsPage       = lazy(() => import("./pages/WorksheetsPage"));
const WorksheetPage        = lazy(() => import("./pages/WorksheetPage"));
const WorksheetSubjectPage = lazy(() => import("./pages/WorksheetSubjectPage"));
const WorksheetSeriesPage  = lazy(() => import("./pages/WorksheetSeriesPage"));
const WorksheetHolidayPage = lazy(() => import("./pages/WorksheetHolidayPage"));
const GamesPage            = lazy(() => import("./pages/GamesPage"));
const GamePage             = lazy(() => import("./pages/GamePage"));
const PublicLegalPage      = lazy(() => import("./pages/PublicLegalPage"));
const TeacherLandingPage   = lazy(() => import("./pages/TeacherLandingPage"));
const NotFound             = lazy(() => import("./pages/NotFound"));
const App             = lazy(() => import("./App"));

function Loading() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner />
    </div>
  );
}

export default function Router() {
  return (
    <Suspense fallback={<Loading />}>
      <ScrollToTop />
      <Routes>
        {/* Landing */}
        <Route path="/"                  element={<MarketingScreen />} />

        {/* Articles */}
        <Route path="/articles"          element={<ArticlesPage />} />
        <Route path="/articles/:slug"    element={<ArticlePage />} />

        {/* Worksheets */}
        <Route path="/worksheets"                  element={<WorksheetsPage />} />
        {/* Subject landing pages — must come before /:id to avoid conflict */}
        <Route path="/worksheets/hebrew"           element={<WorksheetSubjectPage />} />
        <Route path="/worksheets/math"             element={<WorksheetSubjectPage />} />
        <Route path="/worksheets/english"          element={<WorksheetSubjectPage />} />
        <Route path="/worksheets/science"          element={<WorksheetSubjectPage />} />
        <Route path="/worksheets/culture"          element={<WorksheetSubjectPage />} />
        <Route path="/worksheets/series/:type"     element={<WorksheetSeriesPage />} />
        <Route path="/worksheets/holidays"         element={<WorksheetHolidayPage />} />
        <Route path="/worksheets/holidays/:slug"   element={<WorksheetHolidayPage />} />
        <Route path="/worksheets/:id"              element={<WorksheetPage />} />

        {/* Games */}
        <Route path="/games"             element={<GamesPage />} />
        <Route path="/games/:id"         element={<GamePage />} />

        <Route path="/legal"              element={<PublicLegalPage />} />

        {/* Teacher landing */}
        <Route path="/teachers"          element={<TeacherLandingPage />} />

        {/* App SPA (all game / auth / journey logic lives here) */}
        <Route path="/app/*"             element={<App />} />
        <Route path="/login"             element={<Navigate to="/app" replace />} />

        {/* Fallback — friendly 404 instead of silently serving the homepage */}
        <Route path="*"                  element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
